import {PlayerId} from '@glark-newco/game-library';
import {Container, Graphics, Sprite} from '@pixi/react';
import {GlowFilter, OutlineFilter} from 'pixi-filters';
import {Assets, FederatedMouseEvent, Filter, IPointData, Polygon} from 'pixi.js';
import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {FullBodyAvatar} from './FullBodyAvatar';
import {useDraggable} from '../hooks/useDraggable';
import {useIsLinkValid} from '../hooks/useIsLinkValid';
import {
    useActiveEditMode,
    useIsLinkCreationActive,
    useSelectedNodes,
    useSelectNodeForLink,
    useSetNodePopupLocation,
} from '../state/networkEditStore';
import {useMoveNode} from '../state/networkHooks';
import {NodeId} from '../state/networkStore';
import {usePlayerAvatar} from '../state/PlayerAvatarSlice';

interface PlayerSpriteProps {
    playerId?: PlayerId,
    nodeId: NodeId,
    position: IPointData,
}

const outline = new OutlineFilter(4, 0xFFFFFF, 0.5, 1, true);
const glowGreen = new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0x199c24});
const glowRed = new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0x96060b});
const glowBlue = new GlowFilter({distance: 20, outerStrength: 5, alpha:1, color:0x0d27a8});

const hitAreaCords = [-50, -200, -50, 55, 40, 55, 40, -200];

export function DesignPlayer({nodeId, playerId, position}: PlayerSpriteProps): ReactNode {
    const activeEditMode = useActiveEditMode();
    const setPlayerPopup = useSetNodePopupLocation();
    const [hover, setHover] = useState(false);
    const isLinkValid = useIsLinkValid(nodeId);
    const avatar = usePlayerAvatar(playerId || '');
    const moveNode = useMoveNode();

    const isLinkCreationActive = useIsLinkCreationActive();
    const selectedNodes = useSelectedNodes();
    const selectNodeForLink = useSelectNodeForLink();

    const draggableHandlers = useDraggable({callback: (p) => {
        moveNode(nodeId, p.x, p.y);
    }});

    const debugHitArea = false;
    const scale = useMemo(() => (position.y + 500) / 1080, [position.y]);

    const updatePositionInEdit = useCallback((event: FederatedMouseEvent | undefined) => {
        setPlayerPopup(existing => ({pixi: !existing.mantine && event ? nodeId : undefined, mantine: existing.mantine}));
    }, [nodeId, setPlayerPopup]);

    const updateAssignLink = useCallback(() => {
        if (isLinkCreationActive && isLinkValid) {
            selectNodeForLink(nodeId);
        }
    }, [isLinkCreationActive, selectNodeForLink, nodeId, isLinkValid]);

    const filters: Filter[] = useMemo(() => {
        const linkModeHighlight = () => {
            if (!isLinkCreationActive) return [];
            if (hover && !isLinkValid) return [glowRed];
            if (hover && isLinkValid) return [glowGreen];
            if (!hover && selectedNodes.includes(nodeId)) return [glowBlue];
            return [];
        };
        return [
            ...(!playerId ? [outline] : []),
            ...(linkModeHighlight()),
        ];
    }, [playerId, hover, isLinkValid, isLinkCreationActive, selectedNodes]);

    // TV-281" hacky solution to stop player hover filter being present as soon as button clicked - need to change
    useEffect(() => {
        setHover(false);
    }, [activeEditMode]);

    return (
        <Container
            onmouseenter={() => setHover(true)}
            onmouseleave={() => setHover(false)}
            mouseover={updatePositionInEdit}
            mouseout={() => updatePositionInEdit(undefined)}
            hitArea={new Polygon(hitAreaCords)}
            cursor={activeEditMode !== 'none' ? 'inherit' : 'move'}
            alpha={playerId ? 1 : 1}
            anchor={.5} x={position.x} y={position.y} scale={scale}
            filters={filters}
            onclick={updateAssignLink}
            {...(draggableHandlers)}
        >
            {debugHitArea && <Graphics draw={g => g.clear().lineStyle({width: 2.5, color: 0x000000}).drawPolygon(hitAreaCords)}/>}
            {playerId 
                ? <FullBodyAvatar avatar={avatar} position={{x: 0, y: 0}} scale={1}/>
                : <Sprite texture={Assets.get('BaseD')} position={{x: 0, y: 0}} anchor={.5} scale={.5}/>
            }
        </Container>
    );
}
